import { ComponentProps, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import Typography from '@alltrails/core/components/Typography';
import Button from '@alltrails/denali/components/Button';
import Heading from '@alltrails/denali/components/Heading';
import { useModalAriaLabelledBy } from '@alltrails/modal';
import Link from '@alltrails/denali/components/Link';
import { useExperiments } from '@alltrails/experiments';
import AppPromotion from '@alltrails/install-prompt/components/AppPromotion';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import AltLoginsForm from '../AltLoginsForm';
import SectionDivider from '../SectionDivider';
import TermsOfService from '../TermsOfService';
import ContinueInAppButton from './ContinueInAppButton';
import styles from './InitialMobile.module.scss';
import signupModalStyles from './SignUpModal.module.scss';

type InitialProps = Pick<ComponentProps<typeof AltLoginsForm>, 'appleLoginHandler' | 'facebookLoginHandler' | 'googleLoginHandler'> & {
  heading?: ReactNode;
  onContinueWithEmailClick: () => void;
  onLogInClick: () => void;
  deeplinkData?: DeeplinkData;
  displayTrigger: CarouselDisplayTrigger;
  hideAppPromotion?: boolean;
};

// https://www.figma.com/design/wSAbhQvyqMqrzXqrFd6Ylf/Onboarding-2025-Web--Working-?node-id=8181-291667&m=dev
export default function InitialMobile({
  heading,
  onContinueWithEmailClick,
  onLogInClick,
  deeplinkData,
  displayTrigger,
  hideAppPromotion = false,
  ...loginHandlers
}: InitialProps) {
  const ariaLabelledBy = useModalAriaLabelledBy();
  const experiments = useExperiments();
  const isConsolidatedAuth = experiments['web-growth-rm-consolidate-auth']?.value === 'treatment';

  const wrappedHeading = isConsolidatedAuth && heading ? <div className={signupModalStyles.headingMaxWidth}>{heading}</div> : heading;

  return (
    <div className={styles.content}>
      {hideAppPromotion ? (
        <Heading as="h4" id={ariaLabelledBy} size="400">
          {wrappedHeading}
        </Heading>
      ) : (
        <AppPromotion heading={wrappedHeading} />
      )}
      <div>
        {!hideAppPromotion && (
          <>
            <ContinueInAppButton flatColors deeplinkData={deeplinkData} displayTrigger={displayTrigger} />
            <SectionDivider classes={signupModalStyles.divider}>
              <FormattedMessage defaultMessage="or" />
            </SectionDivider>
          </>
        )}
        <AltLoginsForm {...loginHandlers} authType="signup" flatColors />
        <Button
          className={signupModalStyles.email}
          onClick={onContinueWithEmailClick}
          text={<FormattedMessage defaultMessage="Continue with email" />}
          width="full"
        />
      </div>
      <div className={styles.already}>
        {!isConsolidatedAuth && (
          <div>
            <Typography className={signupModalStyles.inline} color="secondary" component="div" variant="text200">
              <FormattedMessage defaultMessage="Already have an account?" />{' '}
            </Typography>
            <Link size="md" onClick={onLogInClick}>
              <FormattedMessage defaultMessage="Log in" />
            </Link>
          </div>
        )}
        <TermsOfService />
      </div>
    </div>
  );
}
